import { css, styled } from '../../../../../../core/theme/styled';

type StyledInputContainerProps = {
  flex?: number;
  flexRow?: boolean;
};

export const StyledInputContainer = styled.div<StyledInputContainerProps>`
  position: relative;
  flex: ${(props) => props.flex || '0 1 auto'};

  & + & {
    margin-top: ${(props) => (props.flexRow ? 0 : props.theme.sizes.xxxsmall)};
  }
`;

export const StyledLabel = styled.label`
  font-size: ${(props) => props.theme.sizes.normal};
  margin-bottom: ${(props) => props.theme.sizes.xxsmall};
  display: block;
  color: ${(props) => props.theme.configuration.primaryTextColor};
`;

type StyledInputProps = {
  error?: boolean;
  floatingLabel?: boolean;
  alignIcon?: boolean; // Align the icon with the input when a floating label exist and not between them.
};

export const InputBaseStyles = css<StyledInputProps>`
  font-size: ${(props) => props.theme.sizes.normal};
  font-family: ${(props) => props.theme.font.face};
  color: ${(props) => props.theme.configuration.primaryTextColor};
  border: 1px solid ${(props) => props.theme.configuration.secondaryTextColor}30;
  border-radius: ${(props) => props.theme.roundedCorners.medium};
  width: 100%;
  padding: ${(props) => props.theme.sizes.normedium} ${(props) => props.theme.sizes.normal};
  background-color: ${(props) => props.theme.configuration.backgroundColor};
  transition: border-color 0.2s, color 0.2s, box-shadow 0.1s;
  outline: none;

  ${({ floatingLabel }) =>
    floatingLabel &&
    css`
      padding-top: 1.75rem;
      padding-bottom: ${(props) => props.theme.sizes.xsmall};

      & + ${StyledLabel} {
        /* Needed to have the click directly into the input, instead of the label */
        pointer-events: none;
      }
    `};

  &:hover {
    border-color: ${(props) => props.theme.configuration.primaryColor};
  }

  &:focus {
    box-shadow: 0 0 0 0.25rem ${(props) => props.theme.configuration.primaryColor}30;
    border-color: ${(props) => props.theme.configuration.primaryColor};
  }

  /**
    * Translate down and scale the label up to cover the placeholder,
    * when following an input (with placeholder-shown support).
    * Also make sure the label is only on one row, at max 2/3rds of the
    * field—to make sure it scales properly and doesn't wrap.
    */

  & + ${StyledLabel} {
    position: absolute;
    top: 0;
    left: 0;
    margin: 0;
    color: ${(props) => props.theme.configuration.secondaryTextColor};
    cursor: text;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    transition: ease 0.2s;
    transform-origin: 0 0;
    transform: translate(
      ${(props) => props.theme.sizes.normedium},
      ${(props) => props.theme.sizes.normedium}
    );
  }

  &:focus + ${StyledLabel} {
    color: ${(props) => props.theme.configuration.primaryColor};
  }

  ${({ error }) =>
    error &&
    css`
      &,
      &:focus,
      &:hover {
        border-color: ${(props) => props.theme.configuration.errorTextColor};
        box-shadow: 0 0 0 0.25rem ${(props) => props.theme.configuration.errorTextColor}30;

        & + ${StyledLabel} {
          color: ${(props) => props.theme.configuration.errorTextColor};
        }
      }
    `};

  &:disabled {
    &,
    &:focus,
    &:hover {
      outline: none;
      color: ${(props) => props.theme.configuration.secondaryTextColor};
      border-color: ${(props) => props.theme.configuration.secondaryTextColor}80;
      background-color: ${(props) => props.theme.configuration.secondaryTextColor}10;

      & + ${StyledLabel} {
        color: ${(props) => props.theme.configuration.secondaryTextColor};
      }
    }
  }
`;

type StyledIconContainerProps = {
  cursor?: 'pointer';
  hide?: boolean;
};

// When this component is set, it always has to be the very first or last element within the InputContainer.
// As default, we assume the icon comes at the end of the input.
export const StyledIconContainer = styled.div<StyledIconContainerProps>`
  display: flex;
  align-items: center;
  height: 100%;
  position: absolute;
  top: 0;
  right: ${(props) => props.theme.sizes.normal};
  color: ${(props) => props.theme.configuration.primaryColor};
  transition: ease 0.2s;
  cursor: ${(props) => props.cursor ?? 'auto'};
  pointer-events: ${(props) => (props.cursor === 'pointer' ? 'auto' : 'none')};

  ${(props) =>
    props.hide &&
    css`
      visibility: hidden;
    `}
`;

export const StyledImageField = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${(props) => props.theme.sizes.normal};
  margin-bottom: ${(props) => props.theme.sizes.normedium};

  img {
    width: 60%;
  }
`;

export const StyledInputCode = styled.input.attrs({ readOnly: true })`
  text-align: center;
  letter-spacing: 6px;
  padding: ${(props) => props.theme.sizes.small};
  font-size: ${(props) => props.theme.sizes.normedium};
  color: ${(props) => props.theme.configuration.primaryTextColor};
  background-color: ${(props) => props.theme.configuration.tintedBackgroundColor};
  border-radius: ${(props) => props.theme.roundedCorners.xsmall};
  border: 1px solid transparent;
  width: 100%;

  :focus {
    outline: none;
    border-color: ${(props) => props.theme.configuration.primaryColor};
  }
`;
